import { type ClipboardEvent, type DragEvent } from "react";
import type {
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectTaskAssignmentSubject,
  CmmdTaskStatus,
  CmmdTaskWorkflow,
  CreateCmmdBoardTaskParams,
  UpdateCmmdBoardTaskParams,
} from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { ProjectTaskDetailContent } from "./ProjectTaskDetailContent";
import { useProjectTaskEditorState } from "./useProjectTaskEditorState";
import type { ProjectTaskWorkbenchDataProps } from "./projectTaskWorkbenchTypes";
import type { resolveTaskThreadHistory } from "~/lib/taskThreadLink";
import { uploadProjectTaskFiles } from "./projectTaskAttachmentUpload";

export interface ProjectTaskDetailEditorProps {
  readonly task: CmmdBoardTask;
  readonly projectTitle: string;
  readonly projectIconUrl: string | null;
  readonly projectIcon: string | null;
  readonly allTasks: readonly CmmdBoardTask[];
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading: boolean;
  readonly agentMembersError: string | null;
  readonly pending: boolean;
  readonly statusMutationDisabled: boolean;
  /** Host cannot write; suppresses write affordances. Defaults to editable. */
  readonly readOnly?: boolean;
  readonly creating: boolean;
  readonly mutationError: string | null;
  readonly executing: boolean;
  readonly hasKnownConversation: boolean;
  readonly workflow: CmmdTaskWorkflow | null;
  readonly threadHistory: ReturnType<typeof resolveTaskThreadHistory>;
  readonly workbenchData: ProjectTaskWorkbenchDataProps;
  readonly onUpdate: (id: number, patch: UpdateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onSetAssignment: (
    id: number,
    subject: CmmdProjectTaskAssignmentSubject,
  ) => Promise<boolean>;
  readonly onCreateTask: (params: CreateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onSetStatus: (id: number, status: CmmdTaskStatus, workflowStageId?: string) => void;
  readonly onStartWork: (task: CmmdBoardTask) => Promise<boolean>;
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
  readonly onOpenTask: (id: number) => void;
  readonly onArchive: () => Promise<boolean>;
  readonly onUnarchive: () => void;
}

export function ProjectTaskDetailEditor(props: ProjectTaskDetailEditorProps) {
  const editor = useProjectTaskEditorState(props.task);
  const { title, description, savedDescription } = editor;
  const commitTitle = async () => {
    const nextTitle = title.trim();
    if (!nextTitle) editor.setTitle(props.task.title);
    else if (nextTitle !== props.task.title) {
      const updated = await props.onUpdate(props.task.id, { title: nextTitle });
      if (!updated) editor.setTitle(nextTitle);
    }
  };
  const startWork = async () => {
    const nextTitle = title.trim() || props.task.title;
    const nextDescription =
      description === (props.task.description ?? "") ? props.task.description : description;
    const patch: UpdateCmmdBoardTaskParams = {
      ...(nextTitle !== props.task.title ? { title: nextTitle } : {}),
      ...(description !== savedDescription ? { description } : {}),
    };
    if (Object.keys(patch).length > 0 && !(await props.onUpdate(props.task.id, patch))) return;
    const started = await props.onStartWork({
      ...props.task,
      title: nextTitle,
      description: nextDescription,
    });
    if (started) await props.workbenchData.reload();
  };
  const saveDescription = async (nextDescription: string) => {
    const updated = await props.onUpdate(props.task.id, { description: nextDescription });
    editor.setDescription(nextDescription);
    if (updated) editor.setSavedDescription(nextDescription);
    return updated;
  };
  const uploadTaskFiles = async (files: readonly File[]) => {
    await uploadProjectTaskFiles(files, props.workbenchData.uploadAttachments);
  };
  const pasteTaskImages = async (event: ClipboardEvent<HTMLElement>) => {
    const files = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (files.length === 0) return;
    event.preventDefault();
    await uploadTaskFiles(files);
  };
  const dropTaskFiles = async (event: DragEvent<HTMLElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) await uploadTaskFiles(files);
  };
  return (
    <ProjectTaskDetailContent
      task={props.task}
      projectTitle={props.projectTitle}
      projectIconUrl={props.projectIconUrl}
      projectIcon={props.projectIcon}
      allTasks={props.allTasks}
      members={props.members}
      agentMembers={props.agentMembers}
      agentMembersLoading={props.agentMembersLoading}
      agentMembersError={props.agentMembersError}
      milestones={props.workbenchData.milestones}
      taskDetail={props.workbenchData.taskDetail}
      attachments={props.workbenchData.attachments}
      comments={props.workbenchData.comments}
      conversations={props.workbenchData.conversations}
      conversationsNextCursor={props.workbenchData.conversationsNextCursor}
      selectedConversation={props.workbenchData.selectedConversation}
      expandedConversationId={props.workbenchData.expandedConversationId}
      conversationsLoaded={props.workbenchData.conversationsLoaded}
      conversationsLoading={props.workbenchData.conversationsLoading}
      loadingMoreConversations={props.workbenchData.loadingMoreConversations}
      loadingConversationId={props.workbenchData.loadingConversationId}
      loadingMoreConversationEvents={props.workbenchData.loadingMoreConversationEvents}
      conversationsError={props.workbenchData.conversationsError}
      conversationDetailError={props.workbenchData.conversationDetailError}
      workSessions={props.workbenchData.workSessions ?? []}
      workSessionsLoaded={props.workbenchData.workSessionsLoaded ?? false}
      workSessionsLoading={props.workbenchData.workSessionsLoading ?? false}
      workSessionsError={props.workbenchData.workSessionsError ?? null}
      taskHistory={props.workbenchData.taskHistory ?? null}
      taskHistoryLoading={props.workbenchData.taskHistoryLoading ?? false}
      taskHistoryError={props.workbenchData.taskHistoryError ?? null}
      activityLoading={props.workbenchData.loading}
      commenting={props.workbenchData.commenting}
      activityError={props.workbenchData.error}
      title={title}
      description={description}
      savedDescription={savedDescription}
      pending={props.pending}
      statusMutationDisabled={props.statusMutationDisabled}
      readOnly={props.readOnly ?? false}
      creating={props.creating}
      mutationError={props.mutationError}
      executing={props.executing}
      hasKnownConversation={props.hasKnownConversation}
      workflow={props.workflow}
      threadHistory={props.threadHistory}
      onTitleChange={editor.setTitle}
      onDescriptionChange={editor.setDescription}
      onCommitTitle={() => void commitTitle()}
      onSaveDescription={saveDescription}
      onUpdate={props.onUpdate}
      onSetAssignment={props.onSetAssignment}
      onCreateTask={props.onCreateTask}
      onCreateComment={props.workbenchData.createComment}
      onUpdateComment={props.workbenchData.updateComment}
      onDeleteComment={props.workbenchData.deleteComment}
      onRetryActivity={() => void props.workbenchData.reload()}
      onEnsureConversationsLoaded={() => void props.workbenchData.ensureConversationsLoaded()}
      onRetryConversations={() => void props.workbenchData.reloadConversations()}
      onLoadMoreConversations={() => void props.workbenchData.loadMoreConversations()}
      onSetStatus={props.onSetStatus}
      onStartWork={() => void startWork()}
      onLoadConversation={(conversationId) =>
        void props.workbenchData.loadConversation(conversationId)
      }
      onLoadMoreConversationEvents={() => void props.workbenchData.loadMoreConversationEvents()}
      onClearConversation={props.workbenchData.clearConversation}
      onLoadTaskHistory={() => {
        void Promise.all([
          props.workbenchData.loadTaskHistory?.(),
          props.workbenchData.ensureConversationsLoaded(),
        ]);
      }}
      onOpenThread={props.onOpenThread}
      onOpenTask={props.onOpenTask}
      onArchive={props.onArchive}
      onUnarchive={props.onUnarchive}
      onPasteTaskImages={(event) => void pasteTaskImages(event)}
      onDropTaskImages={(event) => void dropTaskFiles(event)}
      mobileSection={editor.mobileSection}
      onMobileSectionChange={editor.setMobileSection}
    />
  );
}
